import { Tooltip } from '@nextui-org/react'
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Button } from '~/components/common/Button'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/common/Popover'
import { SearchInput } from '~/components/common/SearchInput'
import { useModal } from '~/components/modals/CurieModal'
import {
  BookmarkVisibility,
  PromptBookmark,
  useDeletePromptBookmark,
  usePromptBookmarks
} from '~/services/promptBookmarkService'
import { BetterToast } from '~/utils/toast'
import { BookmarkEditorModal } from './BookmarkEditorModal'
import { BookmarkIcon, DeleteIcon, EditIcon, LockIcon, PeopleIcon, PlusIcon } from './bookmarkIcons'

export type BookmarksPopoverAlignment = 'landing' | 'chat'

const BOOKMARKS_POPOVER_PLACEMENT: Record<BookmarksPopoverAlignment, 'bottom-start' | 'right-end'> =
  {
    landing: 'bottom-start',
    chat: 'right-end'
  }

interface BookmarksPopoverProps {
  /** Called when the user picks a bookmark — receives the prompt body. */
  onInsert: (body: string) => void
  /** Current draft text (used to prefill the "save current input" flow). */
  currentDraft?: string
  /** Lazily reads the current draft so the closed popover can stay memoized. */
  getCurrentDraft?: () => string
  disabled?: boolean
  alignment?: BookmarksPopoverAlignment
  /** Gap in px between the bookmark trigger and the popover menu. */
  popoverOffset?: number
}

/**
 * In-flow bookmark picker for the chat composer.
 *
 * This is the *popover* version of the bookmark UI — a compact, dense
 * picker that hangs off the chat input so users can drop a saved
 * prompt into the message they're writing. The full curation surface
 * (bigger row cards, search, no length cap) lives in the Bookmarks
 * settings tab, and the heavyweight create/edit/delete flow lives in
 * `BookmarkEditorModal`.
 */
function BookmarksPopoverComponent({
  onInsert,
  currentDraft = '',
  getCurrentDraft,
  disabled,
  alignment = 'landing',
  popoverOffset = 4
}: BookmarksPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const editModal = useModal()
  const [editing, setEditing] = useState<PromptBookmark | null>(null)
  const [createDraft, setCreateDraft] = useState('')

  const placement = BOOKMARKS_POPOVER_PLACEMENT[alignment]

  const handlePick = (b: PromptBookmark) => {
    onInsert(b.body)
    setIsOpen(false)
  }

  const openCreate = () => {
    setEditing(null)
    setCreateDraft(getCurrentDraft?.() ?? currentDraft)
    editModal.open()
    setIsOpen(false)
  }

  const openEdit = (b: PromptBookmark) => {
    setEditing(b)
    editModal.open()
    setIsOpen(false)
  }

  return (
    <>
      {/* Tooltip wraps the Popover (not the trigger button) so NextUI's
          PopoverTrigger.cloneElement still targets the real <button>.
          Putting the Tooltip inside PopoverTrigger breaks click handling. */}
      <Tooltip
        content='Saved Prompts'
        placement='top'
        showArrow
        delay={300}
        closeDelay={0}
        isDisabled={isOpen}
        classNames={{
          base: 'before:bg-[#1f1f1f] z-40',
          content: 'text-sm text-white bg-[#1f1f1f] border-none rounded-xl px-3 py-2 select-none'
        }}
      >
        <div className='inline-flex'>
          <Popover
            isOpen={isOpen}
            onOpenChange={setIsOpen}
            placement={placement}
            offset={popoverOffset}
            shouldFlip={false}
          >
            <PopoverTrigger>
              <button
                type='button'
                disabled={disabled}
                aria-label='Prompt bookmarks'
                data-testid='bookmarks-menu-trigger'
                className={`inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-tokens-neutral-fill-fill-6 disabled:opacity-50 disabled:cursor-not-allowed ${
                  isOpen
                    ? 'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-primary'
                    : 'text-tokens-neutral-text-secondary'
                }`}
              >
                <BookmarkIcon />
              </button>
            </PopoverTrigger>
            {isOpen && (
              <BookmarksPopoverContent
                alignment={alignment}
                placement={placement}
                popoverOffset={popoverOffset}
                onCreate={openCreate}
                onPick={handlePick}
                onEdit={openEdit}
              />
            )}
          </Popover>
        </div>
      </Tooltip>

      {editModal.isOpen && (
        <BookmarkEditorModal
          isOpen={editModal.isOpen}
          onClose={editModal.close}
          editing={editing}
          defaultBody={createDraft}
        />
      )}
    </>
  )
}

export const BookmarksPopover = memo(
  BookmarksPopoverComponent,
  (prev, next) =>
    prev.onInsert === next.onInsert &&
    prev.getCurrentDraft === next.getCurrentDraft &&
    prev.disabled === next.disabled &&
    prev.alignment === next.alignment &&
    prev.popoverOffset === next.popoverOffset &&
    (prev.getCurrentDraft || next.getCurrentDraft ? true : prev.currentDraft === next.currentDraft)
)

function BookmarksPopoverContent({
  alignment,
  placement,
  popoverOffset,
  onCreate,
  onPick,
  onEdit
}: {
  alignment: BookmarksPopoverAlignment
  placement: 'bottom-start' | 'right-end'
  popoverOffset: number
  onCreate: () => void
  onPick: (bookmark: PromptBookmark) => void
  onEdit: (bookmark: PromptBookmark) => void
}) {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<BookmarkVisibility>('personal')
  const [showBottomFade, setShowBottomFade] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const { data, isLoading, error } = usePromptBookmarks()
  const deleteMut = useDeletePromptBookmark()

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const all = data?.bookmarks ?? []
    if (!q) return all
    return all.filter(b => b.title.toLowerCase().includes(q) || b.body.toLowerCase().includes(q))
  }, [data, search])

  const personal = filtered.filter(b => b.visibility === 'personal')
  const shared = filtered.filter(b => b.visibility === 'account')
  const activeItems = activeTab === 'personal' ? personal : shared

  useEffect(() => {
    if (activeTab === 'personal' && personal.length === 0 && shared.length > 0) {
      setActiveTab('account')
    } else if (activeTab === 'account' && shared.length === 0 && personal.length > 0) {
      setActiveTab('personal')
    }
  }, [activeTab, personal.length, shared.length])

  const updateBottomFade = useCallback(() => {
    const el = scrollRef.current
    if (!el) {
      setShowBottomFade(false)
      return
    }

    const hasOverflow = el.scrollHeight > el.clientHeight + 1
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1
    setShowBottomFade(hasOverflow && !isAtBottom)
  }, [])

  useEffect(() => {
    if (activeItems.length === 0) {
      setShowBottomFade(false)
      return
    }

    const raf = window.requestAnimationFrame(updateBottomFade)
    return () => window.cancelAnimationFrame(raf)
  }, [activeItems.length, activeTab, search, updateBottomFade])

  const handleDelete = async (b: PromptBookmark) => {
    // No native confirm in the design system; minimal guard via window.confirm
    // is acceptable for v1 since deletion is only available to the author.
    if (!window.confirm(`Delete bookmark "${b.title}"?`)) return
    try {
      await deleteMut.mutateAsync({ uuid: b.uuid })
      BetterToast.success(`Deleted "${b.title}"`)
    } catch (err) {
      BetterToast.error(err instanceof Error ? err.message : 'Failed to delete bookmark')
    }
  }

  return (
    <PopoverContent
      width={360}
      className='p-2 rounded-[16px] border-0 shadow-popover bg-tokens-neutral-bg-white'
    >
      <div
        className='flex flex-col gap-2'
        data-alignment={alignment}
        data-placement={placement}
        data-offset={popoverOffset}
        data-testid='bookmarks-menu-popover'
      >
        <div className='flex items-center gap-2'>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder='Search...'
            fullWidth
            className='min-w-0 flex-1'
            data-testid='bookmarks-search-input'
          />
          <Button
            variant='secondary'
            onClick={onCreate}
            className='h-8 gap-1.5 rounded-[10px] px-2.5 py-1.5 text-sm shrink-0 [&>span]:h-3.5 [&>span]:w-3.5'
            leftIcon={<PlusIcon />}
            data-testid='bookmarks-new-button'
          >
            New
          </Button>
        </div>

        {isLoading && (
          <div className='text-sm text-tokens-neutral-text-secondary py-4 text-center'>
            Loading…
          </div>
        )}
        {error && (
          <div className='text-sm text-tokens-status-error-text py-4 text-center'>
            Failed to load bookmarks
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <div
            className='text-sm text-tokens-neutral-text-placeholder py-6 text-center'
            data-testid='bookmarks-empty-state'
          >
            {data?.bookmarks?.length
              ? 'No bookmarks match that search.'
              : 'No bookmarks yet. Click + New to save your first prompt.'}
          </div>
        )}

        {(personal.length > 0 || shared.length > 0) && (
          <div className='flex flex-col gap-4 px-2 pb-4' data-testid='bookmarks-list-section'>
            <BookmarkTabs
              activeTab={activeTab}
              onChange={setActiveTab}
              personalCount={personal.length}
              sharedCount={shared.length}
            />
            {activeItems.length > 0 && (
              <div
                ref={scrollRef}
                onScroll={updateBottomFade}
                className={`relative flex max-h-[min(42vh,320px)] flex-col gap-4 overflow-y-auto pr-1 [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-tokens-neutral-border-1 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-tokens-neutral-text-tertiary ${
                  showBottomFade
                    ? '[mask-image:linear-gradient(to_bottom,black_calc(100%-24px),transparent)]'
                    : ''
                }`}
                data-testid='bookmarks-scroll-container'
              >
                {activeItems.map(b => (
                  <BookmarkRow
                    key={b.uuid}
                    bookmark={b}
                    onPick={() => onPick(b)}
                    onEdit={() => onEdit(b)}
                    onDelete={() => handleDelete(b)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </PopoverContent>
  )
}

// ──────────────────────────────────────────────────────────────────────
// Tabs + row (private to the popover).
// ──────────────────────────────────────────────────────────────────────

function BookmarkTabs({
  activeTab,
  onChange,
  personalCount,
  sharedCount
}: {
  activeTab: BookmarkVisibility
  onChange: (tab: BookmarkVisibility) => void
  personalCount: number
  sharedCount: number
}) {
  const tabs: Array<{
    key: BookmarkVisibility
    label: string
    count: number
    widthClass: string
  }> = [
    { key: 'personal', label: 'Personal', count: personalCount, widthClass: 'w-[97px]' },
    {
      key: 'account',
      label: 'Sharing',
      count: sharedCount,
      widthClass: 'w-[85px]'
    }
  ]

  return (
    <div
      className='flex items-start gap-2 border-b border-tokens-neutral-border-2'
      role='tablist'
      aria-label='Bookmark visibility'
      data-testid='bookmarks-visibility-tabs'
    >
      {tabs.map(tab => {
        const selected = activeTab === tab.key
        const disabled = tab.count === 0
        return (
          <button
            key={tab.key}
            type='button'
            role='tab'
            aria-selected={selected}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(tab.key)
            }}
            data-testid={`bookmarks-tab-${tab.key}`}
            className={`inline-flex ${tab.widthClass} min-w-0 items-center justify-center border-b px-4 py-3 text-sm leading-none tracking-[0.5px] transition-colors ${
              selected
                ? 'border-tokens-primary-2 text-tokens-primary-2 font-bold'
                : disabled
                  ? 'cursor-not-allowed border-transparent text-tokens-neutral-text-disabled'
                  : 'border-transparent text-tokens-primary-2 font-normal hover:bg-tokens-neutral-fill-fill-4'
            }`}
          >
            <span className='truncate'>{tab.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function BookmarkRow({
  bookmark,
  onPick,
  onEdit,
  onDelete
}: {
  bookmark: PromptBookmark
  onPick: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div
      className='group flex h-10 w-full items-center rounded-xl hover:bg-tokens-neutral-fill-fill-6 transition-colors'
      data-testid={`bookmark-row-${bookmark.uuid}`}
    >
      <button
        type='button'
        onClick={onPick}
        className='flex h-full min-w-0 flex-1 flex-col justify-center rounded-l-xl px-4 text-left'
        data-testid={`bookmark-pick-${bookmark.uuid}`}
      >
        <div
          className={`text-sm font-medium truncate leading-4 ${
            bookmark.title
              ? 'text-tokens-neutral-text-primary'
              : 'italic text-tokens-neutral-text-secondary'
          }`}
          data-testid={`bookmark-title-${bookmark.uuid}`}
        >
          {bookmark.title || 'Untitled'}
        </div>
        <div
          className='font-mono text-[11px] font-semibold text-tokens-neutral-text-tertiary truncate leading-none tracking-[-0.03em]'
          data-testid={`bookmark-body-${bookmark.uuid}`}
        >
          {bookmark.body}
        </div>
      </button>
      {/* Rightmost column. Owned rows show Edit/Delete (fade in on
          hover, fixed-width so they line up across the list). Shared
          rows we don't own use this same slot for the "shared by X"
          attribution chip — that way the chip sits flush with the row's
          right edge instead of butting up against an empty action area. */}
      {bookmark.is_owner ? (
        <div className='w-[56px] h-full shrink-0 flex items-center justify-end'>
          <div className='opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-4 pr-4'>
            <button
              type='button'
              onClick={onEdit}
              aria-label='Edit bookmark'
              className='inline-flex h-5 w-5 items-center justify-center rounded-md text-tokens-neutral-icon-default hover:text-tokens-neutral-icon-hover hover:bg-tokens-neutral-fill-fill-4 select-none transition-colors'
              data-testid={`bookmark-edit-${bookmark.uuid}`}
            >
              <EditIcon />
            </button>
            <button
              type='button'
              onClick={onDelete}
              aria-label='Delete bookmark'
              className='inline-flex h-5 w-5 items-center justify-center rounded-md text-tokens-neutral-icon-default hover:text-tokens-status-error-text hover:bg-tokens-status-error-10 select-none transition-colors'
              data-testid={`bookmark-delete-${bookmark.uuid}`}
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
      ) : bookmark.visibility === 'account' ? (
        <div
          className='shrink-0 text-[11px] text-tokens-neutral-text-tertiary leading-tight whitespace-nowrap pr-4 pl-2'
          title={bookmark.author_email || undefined}
          data-testid={`bookmark-author-${bookmark.uuid}`}
        >
          shared by {bookmark.author_name || bookmark.author_email || 'a teammate'}
        </div>
      ) : null}
    </div>
  )
}
// ──────────────────────────────────────────────────────────────────────
// Inline "save this prompt as a bookmark" affordance — used as a
// hover-revealed icon button next to past chat messages, so the user
// can capture a good prompt after the fact (the original Salesforce
// workflow that inspired CUR-58).
// ──────────────────────────────────────────────────────────────────────

export function SaveAsBookmarkButton({
  prompt,
  className,
  tooltipPlacement = 'top'
}: {
  prompt: string
  className?: string
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
}) {
  // Prefer the lifted single-instance editor (one modal per ChatDisplay)
  // when its provider is mounted. Falls back to a local modal so the
  // button still works in isolation (e.g. unit tests, embedded
  // previews) without forcing every caller to wrap with a provider.
  const lifted = useContext(BookmarkEditorOpenerContext)
  const localModal = useModal()
  const handleClick = useCallback(() => {
    if (lifted) {
      lifted(prompt)
    } else {
      localModal.open()
    }
  }, [lifted, localModal, prompt])

  return (
    <>
      <Tooltip
        content='Save as bookmark'
        placement={tooltipPlacement}
        showArrow
        delay={300}
        closeDelay={0}
        classNames={{
          base: 'before:bg-[#1f1f1f] z-40',
          content: 'text-sm text-white bg-[#1f1f1f] border-none rounded-xl px-3 py-2 select-none'
        }}
      >
        <button
          type='button'
          onClick={handleClick}
          aria-label='Save prompt as bookmark'
          data-testid='save-as-bookmark-button'
          className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-tokens-neutral-text-tertiary hover:text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6 ${
            className ?? ''
          }`}
        >
          <BookmarkIcon className='w-3.5 h-3.5' />
        </button>
      </Tooltip>
      {/* Local fallback only mounts when no provider is present, so the
          common case (ChatDisplay with the lifted provider) doesn't pay the
          cost of N idle modals per conversation. */}
      {!lifted && (
        <BookmarkEditorModal
          isOpen={localModal.isOpen}
          onClose={localModal.close}
          editing={null}
          defaultBody={prompt}
        />
      )}
    </>
  )
}

// ──────────────────────────────────────────────────────────────────────
// BookmarkEditorProvider — single modal instance per ChatDisplay
//
// Each user message used to mount its own SaveAsBookmarkButton →
// BookmarkEditorModal → 3 mutation hooks. A 50-message conversation
// paid for 50 idle copies of the entire editor. This provider hoists
// the modal to a single instance; SaveAsBookmarkButton just calls
// `openBookmarkEditor(prompt)` to surface it.
// ──────────────────────────────────────────────────────────────────────

type BookmarkEditorOpener = (prompt: string) => void

const BookmarkEditorOpenerContext = createContext<BookmarkEditorOpener | null>(null)

export function BookmarkEditorProvider({ children }: { children: ReactNode }) {
  const modal = useModal()
  const [draft, setDraft] = useState('')

  const open: BookmarkEditorOpener = useCallback(
    prompt => {
      setDraft(prompt)
      modal.open()
    },
    [modal]
  )

  // Stable identity so consumers of the context (every
  // SaveAsBookmarkButton in the conversation) don't re-render when the
  // modal opens/closes. Only the provider itself re-renders.
  const opener = useMemo(() => open, [open])

  return (
    <BookmarkEditorOpenerContext.Provider value={opener}>
      {children}
      <BookmarkEditorModal
        isOpen={modal.isOpen}
        onClose={modal.close}
        editing={null}
        defaultBody={draft}
      />
    </BookmarkEditorOpenerContext.Provider>
  )
}
